import React, { useEffect, useMemo, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import Fade from '@mui/material/Fade';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';

const progressLines = [
    'Reviewing your business requirements...',
    'Engineering conversation guidelines...',
    'Building knowledge usage strategy...',
    'Adding hallucination safeguards...',
    'Optimizing response workflow...',
    'One Prompt Engineer talking to another...'
];

const celebrationChips = [
    'Business Context Captured',
    'Personality Defined',
    'Goals & Guardrails Set'
];

const CELEBRATION_CHIP_INITIAL_DELAY_MS = 2000;
const CELEBRATION_CHIP_STAGGER_MS = 1050;
const CELEBRATION_POST_CHIP_PAUSE_MS = 2900;
const CELEBRATION_DURATION_MS = CELEBRATION_CHIP_INITIAL_DELAY_MS
    + CELEBRATION_CHIP_STAGGER_MS * (celebrationChips.length - 1)
    + CELEBRATION_POST_CHIP_PAUSE_MS;
const ENGINEERING_STEP_DELAYS_MS = [2100, 2300, 2300, 2200, 2300, 2700];
const MIN_ENGINEERING_STAGE_MS = 5600;
const DRAFT_COMPLETE_VISIBLE_MS = 3400;
const OPENING_REVIEW_DELAY_MS = 2000;

export default function PromptEngineerProgress({
    generationStatus,
    errorMessage,
    runId,
    recoveryMode = false,
    recoveryMessage = '',
    onTryAgain,
    onBackToReview,
    backButtonLabel = 'Back to Review',
    onUseBasicDraft,
    onReadyToContinue
}) {
    const readyToContinueRef = useRef(onReadyToContinue);
    const [displayStage, setDisplayStage] = useState('celebration');
    const [visibleCelebrationChipCount, setVisibleCelebrationChipCount] = useState(0);
    const [visibleCount, setVisibleCount] = useState(1);
    const [completedCount, setCompletedCount] = useState(0);
    const [showCompleteState, setShowCompleteState] = useState(false);
    const [showOpeningReviewState, setShowOpeningReviewState] = useState(false);
    const [isEngineeringSequenceComplete, setIsEngineeringSequenceComplete] = useState(false);
    const [engineeringStageStartedAt, setEngineeringStageStartedAt] = useState(0);

    useEffect(() => {
        readyToContinueRef.current = onReadyToContinue;
    }, [onReadyToContinue]);

    useEffect(() => {
        if (!runId) {
            return undefined;
        }

        setDisplayStage('celebration');
        setVisibleCelebrationChipCount(0);

        const chipTimers = celebrationChips.map((_, index) => setTimeout(() => {
            setVisibleCelebrationChipCount(index + 1);
        }, CELEBRATION_CHIP_INITIAL_DELAY_MS + index * CELEBRATION_CHIP_STAGGER_MS));

        const timerId = setTimeout(() => {
            setDisplayStage('engineering');
            setEngineeringStageStartedAt(Date.now());
        }, CELEBRATION_DURATION_MS);

        return () => {
            clearTimeout(timerId);
            chipTimers.forEach((chipTimer) => clearTimeout(chipTimer));
        };
    }, [runId]);

    useEffect(() => {
        if (!runId || displayStage !== 'engineering') {
            return undefined;
        }

        setVisibleCount(1);
        setCompletedCount(0);
        setShowCompleteState(false);
        setShowOpeningReviewState(false);
        setIsEngineeringSequenceComplete(false);

        let cancelled = false;
        let timerId;

        const advance = (index) => {
            if (cancelled) return;

            timerId = setTimeout(() => {
                if (cancelled) return;

                const nextCompletedCount = index + 1;
                setCompletedCount(nextCompletedCount);

                if (nextCompletedCount < progressLines.length) {
                    setVisibleCount(index + 2);
                    advance(index + 1);
                    return;
                }

                setIsEngineeringSequenceComplete(true);
            }, ENGINEERING_STEP_DELAYS_MS[index] || 800);
        };

        advance(0);

        return () => {
            cancelled = true;
            clearTimeout(timerId);
        };
    }, [runId, displayStage]);

    useEffect(() => {
        if (generationStatus !== 'success') {
            return undefined;
        }
        if (!isEngineeringSequenceComplete || showCompleteState) {
            return undefined;
        }

        const elapsed = engineeringStageStartedAt ? Date.now() - engineeringStageStartedAt : 0;
        const minDelayRemaining = Math.max(0, MIN_ENGINEERING_STAGE_MS - elapsed);
        const completionDelay = Math.max(150, minDelayRemaining);

        const completeTimer = setTimeout(() => {
            setCompletedCount(progressLines.length);
            setShowCompleteState(true);
        }, completionDelay);

        return () => clearTimeout(completeTimer);
    }, [generationStatus, isEngineeringSequenceComplete, showCompleteState, engineeringStageStartedAt]);

    useEffect(() => {
        if (!showCompleteState) {
            setShowOpeningReviewState(false);
            return undefined;
        }

        const openingReviewTimer = setTimeout(() => {
            setShowOpeningReviewState(true);
        }, OPENING_REVIEW_DELAY_MS);

        const continueTimer = setTimeout(() => {
            readyToContinueRef.current?.();
        }, DRAFT_COMPLETE_VISIBLE_MS);

        return () => {
            clearTimeout(openingReviewTimer);
            clearTimeout(continueTimer);
        };
    }, [showCompleteState]);

    const isRunning = generationStatus === 'running' || generationStatus === 'success';
    const isError = generationStatus === 'error';
    const renderedTasks = useMemo(() => progressLines.slice(0, visibleCount), [visibleCount]);

    return (
        <Card elevation={2}>
            <CardContent>
                <Stack spacing={2.5}>
                    <Box sx={{ position: 'relative', minHeight: { xs: 520, sm: 470 }, overflow: 'hidden' }}>
                        <Fade in={displayStage === 'celebration'} timeout={{ enter: 380, exit: 300 }}>
                            <Box
                                sx={{
                                    position: 'absolute',
                                    inset: 0,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    pointerEvents: displayStage === 'celebration' ? 'auto' : 'none'
                                }}
                            >
                                <Stack spacing={2} alignItems="center" sx={{ py: 4, textAlign: 'center', width: '100%' }}>
                                    <Box
                                        sx={{
                                            '@keyframes promptEngineerSuccessPulse': {
                                                '0%': { transform: 'scale(0.94)', opacity: 0.82 },
                                                '100%': { transform: 'scale(1)', opacity: 1 }
                                            }
                                        }}
                                    >
                                        <CheckCircleIcon
                                            sx={{
                                                fontSize: 86,
                                                color: 'primary.main',
                                                animation: 'promptEngineerSuccessPulse 420ms ease-out'
                                            }}
                                        />
                                    </Box>
                                    <Typography variant="h5" sx={{ fontWeight: 700 }}>
                                        Thanks For Your Input — Your Interview Is Complete!
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 520 }}>
                                        Your AI Instruction Architect has everything needed to engineer your instructions.
                                    </Typography>
                                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="center" sx={{ mt: 0.5 }}>
                                        {celebrationChips.map((chipLabel, index) => (
                                            <Fade key={chipLabel} in={visibleCelebrationChipCount >= index + 1} timeout={340}>
                                                <Chip
                                                    size="small"
                                                    color="primary"
                                                    label={chipLabel}
                                                    sx={{ fontWeight: 500 }}
                                                />
                                            </Fade>
                                        ))}
                                    </Stack>
                                </Stack>
                            </Box>
                        </Fade>

                        <Fade in={displayStage === 'engineering'} timeout={{ enter: 420, exit: 280 }}>
                            <Box
                                sx={{
                                    position: 'absolute',
                                    inset: 0,
                                    pointerEvents: displayStage === 'engineering' ? 'auto' : 'none'
                                }}
                            >
                                <Stack spacing={2.5} sx={{ py: 1 }}>
                                    <Typography variant="h6">Engineering Your Instructions</Typography>
                                    {recoveryMode && (
                                        <Typography variant="body2" sx={{ color: 'primary.main', fontWeight: 600 }}>
                                            {recoveryMessage || 'Recovering your previous instruction generation...'}
                                        </Typography>
                                    )}
                                    <Typography variant="body2" color="text.secondary">
                                        Your AI Instruction Architect is reviewing your interview answers and engineering a structured instruction draft.
                                    </Typography>
                                    {isRunning ? (
                                        <Stack spacing={1.5}>
                                            <Typography variant="subtitle1">Engineering your rough draft...</Typography>
                                            {renderedTasks.map((line, index) => {
                                                const isComplete = index < completedCount;
                                                return (
                                                    <Stack key={line} direction="row" spacing={1} alignItems="center">
                                                        {isComplete ? (
                                                            <CheckCircleIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                                                        ) : (
                                                            <RadioButtonUncheckedIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                                                        )}
                                                        <Typography variant="body2" color={isComplete ? 'text.primary' : 'text.secondary'}>
                                                            {line}
                                                        </Typography>
                                                    </Stack>
                                                );
                                            })}
                                            <Fade in={showCompleteState} timeout={360}>
                                                <Box>
                                                    <Typography variant="subtitle1" sx={{ color: 'primary.main', fontWeight: 600 }}>
                                                        ✨ Draft Complete!!
                                                        <Box
                                                            component="span"
                                                            sx={{
                                                                ml: 1,
                                                                opacity: showOpeningReviewState ? 1 : 0,
                                                                transition: 'opacity 260ms ease-in-out'
                                                            }}
                                                        >
                                                            ✨ Now Opening Review...
                                                        </Box>
                                                    </Typography>
                                                </Box>
                                            </Fade>
                                        </Stack>
                                    ) : null}
                                </Stack>
                            </Box>
                        </Fade>
                    </Box>

                    {isError && displayStage === 'engineering' ? (
                        <Stack spacing={2}>
                            <Typography variant="body2" color="error.main" sx={{ fontWeight: 600 }}>
                                We had trouble engineering your instructions.
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                You can try again, go back and review your answers, or continue with a basic draft for now.
                            </Typography>
                            {Boolean(errorMessage) && (
                                <Typography variant="caption" color="text.secondary">
                                    {errorMessage}
                                </Typography>
                            )}
                            <Stack direction="row" spacing={2}>
                                <Button variant="contained" onClick={onTryAgain}>
                                    Try Again
                                </Button>
                                <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={onBackToReview}>
                                    {backButtonLabel}
                                </Button>
                                <Button variant="text" onClick={onUseBasicDraft}>
                                    Use Basic Draft
                                </Button>
                            </Stack>
                        </Stack>
                    ) : null}
                </Stack>
            </CardContent>
        </Card>
    );
}
